
import React, { useState, useEffect } from 'react';
import { Product, Address } from '../types';
import { Button } from '../components/ui/Button';
import { supabase } from '../services/supabaseClient';
import { ChevronRight, CreditCard, ShoppingBag, Truck, CheckCircle, AlertCircle, Trash2, Plus, Minus, Lock, ShieldCheck, Zap, Clock } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

// Declaración global para TS ya que cargamos Stripe en index.html
declare global {
  interface Window {
    Stripe: any;
  }
}

interface CheckoutProps {
  cart: Product[];
  setCart: (cart: Product[]) => void;
  clearCart: () => void;
  onNavigate: (page: string) => void;
  session: any;
}

export const Checkout: React.FC<CheckoutProps> = ({ cart, setCart, clearCart, onNavigate, session }) => {
  // Usar la anon key como Bearer token para las Edge Functions
  // Esto evita el error 401 Invalid JWT cuando la sesión del usuario expira
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  const fnHeaders = { Authorization: `Bearer ${anonKey}` };

  const [step, setStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<any>({});
  const [stripeObj, setStripeObj] = useState<any>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bizum' | 'paypal' | 'manual'>('card');
  const [isLocalPickup, setIsLocalPickup] = useState(false);

  const [address, setAddress] = useState<Address>(() => {
    const saved = localStorage.getItem('checkout_address');
    return saved ? JSON.parse(saved) : {
      fullName: '',
      street: '',
      city: '',
      zipCode: '',
      country: 'España',
      phone: ''
    };
  });



  useEffect(() => {
    localStorage.setItem('checkout_address', JSON.stringify(address));
  }, [address]);


  // Cálculos de totales
  const subtotal = cart.reduce((acc, item) => acc + (item.price || 0), 0);
  const isNational = address.country.toLowerCase().trim() === 'españa' || address.country.toLowerCase().trim() === 'spain';
  const calculatedShippingCost = cart.reduce((acc, item) => {
    const cost = isNational ? (item.shipping_cost_national || 0) : (item.shipping_cost_international || 0);
    return Math.max(acc, cost); // Cobramos el envío más caro del carrito
  }, 0);
  const shippingCost = isLocalPickup ? 0 : calculatedShippingCost;
  const finalTotal = subtotal + shippingCost;

  // 1. Cargar Configuración al inicio
  useEffect(() => {
    const fetchConfig = async () => {
      let finalConfig = {
        stripe_public_key: 'pk_test_51O8R7qEw2wDXZH9cW8EzvG5lD2eOqO8g6qj5vW5D2', // Fallback key
        paypal_client_id: process.env.REACT_APP_PAYPAL_CLIENT_ID || '' // Intentamos usar el .env local primero
      };

      try {
        const { data } = await supabase.from('site_config').select('key, value').eq('group_name', 'pagos');
        if (data && data.length > 0) {
          const configMap = data.reduce((acc: any, curr) => ({ ...acc, [curr.key]: curr.value }), {});
          finalConfig = { ...finalConfig, ...configMap }; // Combina, dando prioridad a la BD
        }
      } catch (err) {
        console.warn("Fallo cargando site_config pagos, usando fallbacks", err);
      }

      // Si finalConfig indica el valor de testing de Supabase, forzamos usar 'test'
      if (finalConfig.paypal_client_id === 'test_paypal_client_id') {
        finalConfig.paypal_client_id = 'test';
      }

      // Si no tenemos Client ID válido, le pedimos el real al Edge Function (solo en produccion)
      if (!finalConfig.paypal_client_id || finalConfig.paypal_client_id === '') {
        try {
          console.log("Fetching PayPal Client ID from Edge Function...");
          const { data: ppData, error: ppError } = await supabase.functions.invoke('paypal-checkout', {
            body: { action: 'get_client_id' },
            headers: fnHeaders
          });
          if (!ppError && ppData?.clientId) {
            finalConfig.paypal_client_id = ppData.clientId;
          }
        } catch (e) {
          console.error("Error fetching PayPal Client ID", e);
        }
      }

      setConfig(finalConfig);
    };
    fetchConfig();
  }, []);

  // 2. Inicializar Stripe cuando tenemos la Key
  useEffect(() => {
    if (config.stripe_public_key && window.Stripe && !stripeObj) {
      try {
        const stripe = window.Stripe(config.stripe_public_key);
        setStripeObj(stripe);
      } catch (err) {
        console.error("Error inicializando Stripe:", err);
      }
    }
  }, [config.stripe_public_key]);

  // 3. Montar Stripe redirigiendo a su Checkout alojado
  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!session) {
      alert('Debes iniciar sesión para completar la compra.');
      onNavigate('auth');
      return;
    }

    if (paymentMethod === 'card') {
      setLoading(true);
      setPaymentError(null);

      try {
        // 1. Sincronización proactiva: Buscar en la DB local
        let processedCart = [...cart];
        const productIds = cart.map(item => item.id);
        const { data: syncedProducts } = await supabase
          .from('productos')
          .select('*')
          .in('id', productIds);

        if (syncedProducts) {
          processedCart = cart.map(item => {
            const synced = syncedProducts.find(p => p.id === item.id);
            return {
              ...item,
              ...synced, // Actualizamos todos los campos, incluyendo stripe_price_id
              stripe_price_id: synced?.stripe_price_id || item.stripe_price_id,
              price: synced?.precio || item.price // También asegurar precio actualizado
            };
          });
        }

        // 2. Si todavía falta algún stripe_price_id, intentamos crearlo llamando a stripe-sync
        for (let i = 0; i < processedCart.length; i++) {
          if (!processedCart[i].stripe_price_id) {
            console.log(`Syncing missing product to Stripe: ${processedCart[i].name}`);
            const p = processedCart[i] as any;
            const payloadForSync = {
              ...processedCart[i],
              nombre: processedCart[i].name || p.nombre,
              precio: processedCart[i].price || p.precio,
              imagen_url: processedCart[i].image_url || p.imagen_url,
              descripcion: processedCart[i].description || p.descripcion
            };
            const { data: syncData, error: syncError } = await supabase.functions.invoke('stripe-sync', {
              body: { action: 'sync_product', payload: payloadForSync },
              headers: fnHeaders
            });

            if (syncError) {
              console.error("Error from stripe-sync Function:", syncError);
              throw new Error("No se pudo sincronizar el producto con Stripe.");
            }

            if (syncData?.stripe_price_id) {
              processedCart[i].stripe_price_id = syncData.stripe_price_id;
              // Actualizar la DB local para que no vuelva a ocurrir
              await supabase.from('productos').update({
                stripe_price_id: syncData.stripe_price_id
              }).eq('id', processedCart[i].id);
            } else if (syncData?.error) {
              console.error("Stripe sync error data:", syncData.error);
              throw new Error(`Error de sincronización: ${syncData.error}`);
            }
          }
        }

        const itemsForStripe = processedCart.map(item => ({
          stripe_price_id: item.stripe_price_id,
          quantity: item.quantity || 1
        })).filter(item => item.stripe_price_id);

        if (itemsForStripe.length !== processedCart.length) {
          const failedItems = processedCart.filter(item => !item.stripe_price_id).map(i => i.name).join(', ');
          throw new Error(`Faltan credenciales o error de sincronización en Stripe para: ${failedItems}`);
        }

        const payloadBody = {
          items: itemsForStripe,
          successUrl: `${window.location.origin}/success`,
          cancelUrl: `${window.location.origin}/cancel`,
          shippingCost: shippingCost,
          paymentMethodTypes: [paymentMethod] // Only 'card' or 'bizum'
        };

        const { data, error } = await supabase.functions.invoke('stripe-checkout', {
          body: payloadBody,
          headers: fnHeaders
        });

        if (error) {
          console.error('Stripe Invoke Error:', error);
          let rawError = "No context available";
          if (error.context) {
            try {
              rawError = await error.context.text();
            } catch (e) { }
          }
          throw new Error(`Edge Function Error: ${error.message} | Detalles: ${rawError}`);
        }

        if (!data?.url) {
          throw new Error("Stripe no devolvió una URL válida.");
        }

        // Redirigir al Hosted Checkout de Stripe
        window.location.href = data.url;
      } catch (err: any) {
        console.error("Stripe Checkout Crash:", err);
        setPaymentError(`Detalle del error Stripe: ${err.message || 'Desconocido'}`);
        setLoading(false);
      }
    }
  };

  const simulateOrder = async () => {
    setLoading(true);
    // Simular retardo
    setTimeout(() => saveOrderToDB('stripe', 'sim_' + Math.random().toString(36).substring(7)), 1500);
  };

  const saveOrderToDB = async (method: string, paymentId: string) => {
    try {
      // Guardar en la tabla 'pedidos'
      const { data: order, error: orderError } = await supabase
        .from('pedidos')
        .insert({
          user_id: session.user.id,
          cliente_email: session.user.email,
          cliente_nombre: address.fullName,
          total: finalTotal,
          moneda: 'eur',
          estado: (method === 'bizum_manual' || method === 'manual') ? 'pendiente' : 'pagado',
          direccion_envio: address,
          costo_envio: shippingCost,
          stripe_session_id: paymentId,
          gateway: method
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Insertar items y deducir stock
      const orderItems = cart.map(item => ({
        pedido_id: order.id,
        producto_id: item.id,
        cantidad: 1,
        precio_unitario: item.price || 0
      }));

      const { error: itemsError } = await supabase.from('pedido_items').insert(orderItems);
      if (itemsError) throw itemsError;

      // Deducir stock localmente si no usa webhook (demo)
      for (const item of cart) {
        if (item.stock && item.stock > 0) {
          await supabase.from('productos').update({ stock: item.stock - 1 }).eq('id', item.id);
        }
      }

      clearCart();
      localStorage.removeItem('checkout_step'); // Resetear solo el paso, conservamos info de envio util
      alert('¡Pago realizado con éxito! Gracias por tu compra.');
      onNavigate('home');
    } catch (err: any) {
      setPaymentError(err.message);
    } finally {
      setLoading(false);
    }
  }


  // RENDER
  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 px-4 text-center bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-sm max-w-md mx-auto">
          <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-serif font-bold mb-2">Tu carrito está vacío</h2>
          <Button onClick={() => onNavigate('shop')}>Ir a la Tienda</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-[#F5F1E8] px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-serif font-bold text-mabelita-dark mb-8 text-center">Finalizar Compra</h1>

        {/* Steps Indicator */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-mabelita-primary text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
            <div className={`w-20 h-1 ${step >= 2 ? 'bg-mabelita-primary' : 'bg-gray-200'}`}></div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-mabelita-primary text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
            <div className={`w-20 h-1 ${step >= 3 ? 'bg-mabelita-primary' : 'bg-gray-200'}`}></div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 3 ? 'bg-mabelita-primary text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="md:col-span-2 space-y-6">

            {/* STEP 1: RESUMEN */}
            {step === 1 && (
              <div className="bg-white p-6 rounded-2xl shadow-sm animate-in fade-in slide-in-from-left-4">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <ShoppingBag className="text-mabelita-primary" /> Resumen del Pedido
                </h2>
                <div className="space-y-4">
                  {cart.reduce((acc: any[], item) => {
                    const existing = acc.find(i => i.id === item.id);
                    if (existing) {
                      existing.quantity += 1;
                    } else {
                      acc.push({ ...item, quantity: 1 });
                    }
                    return acc;
                  }, []).map((item, i) => (
                    <div key={i} className="flex gap-4 border-b border-gray-100 pb-4 last:border-0 relative">
                      <img src={item.image_url} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.description}</p>

                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => {
                            const indexToRemove = cart.findIndex(c => c.id === item.id);
                            if (indexToRemove !== -1) setCart(cart.filter((_, idx) => idx !== indexToRemove));
                          }} className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">-</button>
                          <span className="font-medium text-sm">{item.quantity}</span>
                          <button onClick={() => {
                            const itemToAdd = cart.find(c => c.id === item.id);
                            if (itemToAdd && item.quantity < (item.stock || 99)) setCart([...cart, itemToAdd]);
                          }} className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">+</button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <div className="font-bold text-mabelita-primary">€{((item.price || 0) * item.quantity).toFixed(2)}</div>
                        {(item.stock > 0 && item.stock <= 5) && (
                          <span className="text-[10px] text-red-500 font-bold">¡Últimas {item.stock}!</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {cart.length > 0 && (
                  <div className="mt-6 flex justify-between items-center bg-gray-50 p-4 rounded-xl">
                    <div className="text-gray-500 font-medium">Subtotal:</div>
                    <div className="text-xl font-bold">€{subtotal.toFixed(2)}</div>
                  </div>
                )}
                {/* El costo de envío se calculará en el siguiente paso */}
                <div className="mt-6 flex justify-end">
                  <Button onClick={() => setStep(2)}>Continuar a Envío</Button>
                </div>
              </div>
            )}

            {/* STEP 2: DIRECCIÓN */}
            {step === 2 && (
              <div className="bg-white p-6 rounded-2xl shadow-sm animate-in fade-in slide-in-from-left-4">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Truck className="text-mabelita-primary" /> Datos de Envío
                </h2>

                <div className="flex gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setIsLocalPickup(false)}
                    className={`flex-1 p-4 border rounded-xl flex flex-col items-center justify-center gap-2 font-bold transition-all ${!isLocalPickup ? 'border-mabelita-primary bg-mabelita-primary/5 text-mabelita-primary ring-2 ring-mabelita-primary/20' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    Envío a Domicilio
                    <span className="text-xs font-normal">Calculado en el Total</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsLocalPickup(true)}
                    className={`flex-1 p-4 border rounded-xl flex flex-col items-center justify-center gap-2 transition-all ${isLocalPickup ? 'border-green-500 bg-green-50 text-green-800 ring-2 ring-green-500/20' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    <span className="font-bold">Recogida en Taller</span>
                    <span className="text-xs">Gratis (En persona)</span>
                  </button>
                </div>

                <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={(e) => { e.preventDefault(); setStep(3); }}>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Nombre Completo</label>
                    <input required minLength={3} maxLength={100} pattern="^[a-zA-ZÀ-ÿ\s]{3,100}$" title="El nombre debe contener al menos 3 caracteres y solo letras." type="text" value={address.fullName} onChange={e => setAddress({ ...address, fullName: e.target.value })} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-mabelita-primary/20 outline-none" placeholder="Ej: Maria Lopez" />
                  </div>
                  {!isLocalPickup && (
                    <>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Dirección (Calle, número, piso)</label>
                        <input required={!isLocalPickup} minLength={5} maxLength={200} type="text" value={address.street} onChange={e => setAddress({ ...address, street: e.target.value })} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-mabelita-primary/20 outline-none" placeholder="Ej: Calle Principal 123, Piso 2A" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Ciudad</label>
                        <input required={!isLocalPickup} minLength={2} maxLength={100} type="text" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-mabelita-primary/20 outline-none" placeholder="Ej: Madrid" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">País</label>
                        <select value={address.country} onChange={e => setAddress({ ...address, country: e.target.value })} className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-mabelita-primary/20 outline-none">
                          <option value="España">España</option>
                          <option value="Europa">Europa (UE)</option>
                          <option value="Internacional">Internacional (Resto del Mundo)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Código Postal</label>
                        <input required={!isLocalPickup} pattern="^[0-9A-Za-z\-]{3,10}$" title="Introduce un código postal válido (ej: 28001)" type="text" value={address.zipCode} onChange={e => setAddress({ ...address, zipCode: e.target.value })} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-mabelita-primary/20 outline-none" placeholder="Ej: 28001" />
                      </div>
                    </>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Teléfono Móvil {isLocalPickup ? '(Para contactarte)' : '(Para la agencia de reparto)'}</label>
                    <input required pattern="^\+?[0-9\s\-]{9,15}$" title="Introduce un número de teléfono válido (mín. 9 dígitos)" type="tel" value={address.phone} onChange={e => setAddress({ ...address, phone: e.target.value })} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-mabelita-primary/20 outline-none" placeholder="Ej: +34 600 000 000" />
                  </div>
                  <div className="md:col-span-2 flex justify-between mt-4">
                    <button type="button" onClick={() => setStep(1)} className="text-gray-500 hover:underline">Volver</button>
                    <Button type="submit">Continuar al Pago</Button>
                  </div>
                </form>
              </div>
            )}

            {/* STEP 3: PAGO */}
            {step === 3 && (
              <div className="bg-white p-6 rounded-2xl shadow-sm animate-in fade-in slide-in-from-left-4">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="text-mabelita-primary" /> Selecciona tu Método de Pago
                </h2>

                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex-1 p-4 border rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${paymentMethod === 'card' ? 'border-mabelita-primary bg-mabelita-primary/5 text-mabelita-primary ring-2 ring-mabelita-primary/20' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    <CreditCard size={20} /> Tarjeta
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bizum')}
                    className={`flex-1 p-4 border rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${paymentMethod === 'bizum' ? 'border-teal-500 bg-teal-50 text-teal-700 ring-2 ring-teal-500/20' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    <div className="font-sans font-extrabold text-[#00c4b3] flex items-center gap-1"><span className="text-xl">bizum</span></div>
                  </button>
                  {config.paypal_client_id && config.paypal_client_id.length >= 4 && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`flex-1 p-4 border rounded-xl flex items-center justify-center gap-2 transition-all ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50 text-blue-800 ring-2 ring-blue-500/20' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg" alt="PayPal" className="h-6" />
                      <span className="font-bold text-sm">PayPal</span>
                    </button>
                  )}
                </div>

                {/* Error Display Global (Solo uno) */}
                {paymentError && (
                  <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl mb-6 flex items-start gap-3 animate-in fade-in zoom-in-95">
                    <AlertCircle size={22} className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium leading-relaxed">{paymentError}</p>
                  </div>
                )}

                {/* Stripe Action Button */}
                {paymentMethod === 'card' && (
                  <div className="space-y-6 animate-in fade-in">
                    <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-200">
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Estás a un paso de completar tu pedido. Al pulsar el botón, serás redirigido a la pasarela segura de **Stripe** donde podrás pagar de forma segura con Tarjeta.
                      </p>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t">
                      <button type="button" onClick={() => setStep(2)} className="text-gray-500 hover:underline font-medium" disabled={loading}>Volver al envío</button>
                      <Button type="button" onClick={handlePlaceOrder} disabled={loading} className="w-full md:w-auto px-10 py-6 h-auto text-lg">
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Clock className="animate-spin" size={20} />
                            Procesando...
                          </span>
                        ) : `Pagar €${finalTotal.toFixed(2)}`}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Bizum Manual Container */}
                {paymentMethod === 'bizum' && (
                  <div className="space-y-6 animate-in fade-in pt-4">
                    <div className="bg-[#00c4b3]/10 p-6 rounded-xl border border-[#00c4b3]/20 text-center">
                      <h4 className="font-bold text-[#009b8d] mb-2 text-lg">Pago manual por Bizum</h4>
                      <p className="text-[#007a6f] text-sm mb-4">
                        Para pagar por Bizum, envía exactamente <b>€{finalTotal.toFixed(2)}</b> al número:<br />
                        <span className="text-3xl font-black block my-3 tracking-wider">{config.bizum_phone || '+34 600 000 000'}</span>
                        con el concepto: <br /><b className="text-lg bg-white px-2 py-1 rounded mt-2 inline-block shadow-sm text-gray-800">Pedido de {address.fullName.split(' ')[0] || 'Tienda'}</b>
                      </p>
                      <p className="text-xs text-[#009b8d] font-medium mt-4">Una vez enviado el Bizum, pulsa el botón abajo para confirmar tu pedido. Lo enviaremos tras verificar el pago.</p>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t">
                      <button type="button" onClick={() => setStep(2)} className="text-gray-500 hover:underline font-medium" disabled={loading}>Volver al envío</button>
                      <Button type="button" onClick={() => saveOrderToDB('bizum_manual', 'bizum_' + Date.now())} disabled={loading} className="w-full md:w-auto px-6 py-6 h-auto text-lg bg-[#00c4b3] hover:bg-[#009b8d] text-white border-0">
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Clock className="animate-spin" size={20} />
                            Guardando...
                          </span>
                        ) : `He enviado el Bizum de €${finalTotal.toFixed(2)}`}
                      </Button>
                    </div>
                  </div>
                )}

                {/* PayPal Container */}
                {paymentMethod === 'paypal' && (
                  <div className="space-y-6 animate-in fade-in pt-4 min-h-[150px]">
                    {config.paypal_client_id ? (
                      <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "EUR", intent: "capture" }}>
                        <PayPalButtons
                          style={{ layout: "vertical", shape: "rect", color: "blue" }}
                          createOrder={async (data, actions) => {
                            // Llamar a nuestra Edge Function para crear el pedido de PayPal
                            const { data: orderData, error } = await supabase.functions.invoke('paypal-checkout', {
                              body: { amount: finalTotal, action: 'create' },
                              headers: fnHeaders
                            });

                            if (error || !orderData?.id) {
                              setPaymentError("No se pudo conectar con PayPal para iniciar el pago.");
                              throw new Error("Error creating PayPal order");
                            }
                            return orderData.id;
                          }}
                          onApprove={async (data, actions) => {
                            setLoading(true);
                            setPaymentError(null);

                            // Llamar a nuestra Edge Function para capturar el pago
                            // Y pedirle que guarde el pedido (así es seguro)
                            const { data: captureData, error } = await supabase.functions.invoke('paypal-checkout', {
                              body: {
                                orderID: data.orderID,
                                action: 'capture',
                                cart: cart,
                                user_id: session.user.id,
                                email: session.user.email,
                                address: address,
                                amount: finalTotal,
                                shippingCost: shippingCost
                              },
                              headers: fnHeaders
                            });

                            if (error || captureData?.status !== 'COMPLETED') {
                              setPaymentError("Error al capturar el pago con PayPal.");
                              setLoading(false);
                              return;
                            }

                            clearCart();
                            localStorage.removeItem('checkout_step');
                            alert('¡Pago realizado con éxito! Gracias por tu compra.');
                            onNavigate('home');
                          }}
                          onCancel={() => {
                            setPaymentError("Aun no has completado el pago con PayPal.");
                          }}
                          onError={(err) => {
                            console.error("PayPal Error:", err);
                            setPaymentError("Hubo un error con la ventana de PayPal.");
                          }}
                        />
                      </PayPalScriptProvider>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">PayPal no está configurado actualmente.</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t mt-6">
                      <button type="button" onClick={() => setStep(2)} className="text-gray-500 hover:underline" disabled={loading}>Volver</button>
                    </div>
                  </div>
                )}

                {!config.stripe_public_key && paymentMethod === 'manual' && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">La pasarela de Stripe no está configurada.</p>
                    <Button onClick={simulateOrder} variant="secondary">Simular Pedido (Modo Demo)</Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resumen Lateral */}
          <div className="md:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-sm sticky top-24">
              <h3 className="font-serif font-bold text-xl mb-4">Resumen</h3>
              <div className="space-y-2 text-sm mb-4 pb-4 border-b border-gray-100">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Envío {isNational ? '(Nacional)' : '(Internacional)'}</span>
                  <span className={shippingCost === 0 ? "text-green-600 font-medium" : "text-gray-800"}>
                    {shippingCost === 0 ? 'Gratis' : `€${shippingCost.toFixed(2)}`}
                  </span>
                </div>
              </div>
              <div className="flex justify-between text-xl font-bold text-mabelita-dark mb-6">
                <span>Total</span>
                <span>€{finalTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
                <Lock size={12} /> SSL 256-bit Seguro
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
